import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAppSelector } from '../redux/hooks';
import {
  useListSiteEventsQuery, useGetSiteIntelligenceQuery,
  type EventType, type EventStatus,
  type SeverityLevel, type SiteEvent,
} from '../redux/api/eventsApi';
import {
  useListSiteTasksQuery,
  useGetTaskStatisticsQuery,
  type SiteTask, type TaskStatus, type TaskStatistics,
} from '../redux/api/tasksApi';
import { useListSiteThemesQuery } from '../redux/api/themesApi';

export type ViewMode = 'tasks' | 'events';

export function useEventsTasksTab() {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || '';

  const [viewMode, setViewMode] = useState<ViewMode>('tasks');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [componentFilter, setComponentFilter] = useState('');

  const [taskStatusFilter, setTaskStatusFilter] = useState<TaskStatus | ''>('');
  const [taskSortBy, setTaskSortBy] = useState('event_date');
  const [taskSortDir, setTaskSortDir] = useState<'asc' | 'desc'>('desc');

  const [eventType, setEventType] = useState<EventType | ''>('');
  const [severity, setSeverity] = useState<SeverityLevel | ''>('');
  const [eventStatus, setEventStatus] = useState<EventStatus | ''>('');
  const [themeId, setThemeId] = useState('');

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [showDashboard, setShowDashboard] = useState(true);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const backendSortBy = taskSortBy === 'event_date' ? undefined : taskSortBy;
  const { data: tasksData, isLoading: tasksLoading } = useListSiteTasksQuery(
    {
      siteId,
      ...(taskStatusFilter && { status: taskStatusFilter }),
      ...(debouncedSearch && viewMode === 'tasks' ? { search: debouncedSearch } : {}),
      ...(backendSortBy && { sort_by: backendSortBy, sort_dir: taskSortDir }),
      limit: 100,
    },
    { skip: !siteId }
  );

  const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useListSiteEventsQuery(
    {
      siteId,
      ...(eventType && { event_type: eventType }),
      ...(severity && { severity }),
      ...(eventStatus && { status: eventStatus }),
      ...(themeId && { theme_id: themeId }),
      ...(debouncedSearch && viewMode === 'events' ? { search: debouncedSearch } : {}),
      sort_by: 'occurred_at',
      sort_dir: 'desc',
      limit: 100,
    },
    { skip: !siteId }
  );

  const { data: themesData } = useListSiteThemesQuery({ siteId }, { skip: !siteId });
  const { data: intelData } = useGetSiteIntelligenceQuery(
    { siteId, eventsLimit: 200, tasksLimit: 200 },
    { skip: !siteId }
  );
  const { data: taskStats, isLoading: taskStatsLoading } = useGetTaskStatisticsQuery(
    { siteId },
    { skip: !siteId }
  );

  const events = eventsData?.items || [];
  const tasks = tasksData?.items || [];
  const themes = themesData?.items || [];

  const { taskComponentMap, eventComponentMap, allComponents, taskEventDateMap } = useMemo(() => {
    const tcMap = new Map<string, string[]>();
    const ecMap = new Map<string, string[]>();
    const comps = new Map<string, { type: string; count: number }>();
    const tedMap = new Map<string, string>();

    if (!intelData) return { taskComponentMap: tcMap, eventComponentMap: ecMap, allComponents: comps, taskEventDateMap: tedMap };

    const eventDateMap = new Map<string, string>();
    intelData.events.forEach((evt) => {
      eventDateMap.set(evt.id, evt.occurred_at);
      const identifiers = evt.affected_entities.map((e) => e.entity_identifier);
      ecMap.set(evt.id, identifiers);
      identifiers.forEach((ident) => {
        const entityType = evt.affected_entities.find((e) => e.entity_identifier === ident)?.entity_type || '';
        const existing = comps.get(ident);
        if (existing) {
          existing.count++;
        } else {
          comps.set(ident, { type: entityType, count: 1 });
        }
      });
    });

    intelData.tasks.forEach((task) => {
      if (task.source_event_id) {
        const components = ecMap.get(task.source_event_id);
        if (components) {
          tcMap.set(task.id, components);
        }
        const eventDate = eventDateMap.get(task.source_event_id);
        if (eventDate) {
          tedMap.set(task.id, eventDate);
        }
      }
    });

    return { taskComponentMap: tcMap, eventComponentMap: ecMap, allComponents: comps, taskEventDateMap: tedMap };
  }, [intelData]);

  const filteredTasks = useMemo(() => {
    let result = tasks;

    if (componentFilter) {
      result = result.filter((task) => {
        const components = taskComponentMap.get(task.id);
        return components?.includes(componentFilter);
      });
    }

    if (taskSortBy === 'event_date') {
      result = [...result].sort((a, b) => {
        const dateA = taskEventDateMap.get(a.id) || a.created_at;
        const dateB = taskEventDateMap.get(b.id) || b.created_at;
        const cmp = new Date(dateA).getTime() - new Date(dateB).getTime();
        return taskSortDir === 'desc' ? -cmp : cmp;
      });
    }

    return result;
  }, [tasks, componentFilter, taskComponentMap, taskSortBy, taskSortDir, taskEventDateMap]);

  const filteredEvents = useMemo(() => {
    if (!componentFilter) return events;
    return events.filter((event) => {
      const components = eventComponentMap.get(event.id);
      return components?.includes(componentFilter);
    });
  }, [events, componentFilter, eventComponentMap]);

  const eventStats = useMemo(() => {
    const stats = {
      total: events.length,
      bySeverity: {} as Record<string, number>,
      byType: {} as Record<string, number>,
      byStatus: {} as Record<string, number>,
      totalEnergyImpactKwh: 0,
      totalCapacityAffectedKw: 0,
      unresolved: 0,
    };
    events.forEach((e) => {
      stats.bySeverity[e.severity] = (stats.bySeverity[e.severity] || 0) + 1;
      stats.byType[e.event_type] = (stats.byType[e.event_type] || 0) + 1;
      stats.byStatus[e.status] = (stats.byStatus[e.status] || 0) + 1;
      if (e.energy_impact_kwh != null) stats.totalEnergyImpactKwh += e.energy_impact_kwh;
      if (e.capacity_affected_kw != null) stats.totalCapacityAffectedKw += e.capacity_affected_kw;
      if (e.status !== 'resolved' && e.status !== 'closed') stats.unresolved++;
    });
    return stats;
  }, [events]);

  const handleComponentClick = useCallback((comp: string) => {
    setComponentFilter((prev) => prev === comp ? '' : comp);
  }, []);

  const handleEventClick = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
  }, []);

  const handleOpenTimeline = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'interactivetimeline' } }));
  }, []);

  // Listen for external navigation requests (e.g., from dashboard modules)
  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.eventId) {
        setViewMode('events');
        setSelectedEventId(detail.eventId);
      } else if (detail?.taskId) {
        setViewMode('tasks');
        setExpandedTaskId(detail.taskId);
      }
    };
    window.addEventListener('navigateEventsTasksTab', handleNavigate);
    return () => window.removeEventListener('navigateEventsTasksTab', handleNavigate);
  }, []);

  const selectClass = "h-8 px-2 text-xs rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]";
  const isLoading = viewMode === 'tasks' ? tasksLoading : eventsLoading;
  const activeItemCount = viewMode === 'tasks' ? filteredTasks.length : filteredEvents.length;
  const totalItemCount = viewMode === 'tasks' ? tasks.length : events.length;

  return {
    siteId,
    viewMode,
    setViewMode,
    search,
    setSearch,
    componentFilter,
    setComponentFilter,
    taskStatusFilter,
    setTaskStatusFilter,
    taskSortBy,
    setTaskSortBy,
    taskSortDir,
    setTaskSortDir,
    eventType,
    setEventType,
    severity,
    setSeverity,
    eventStatus,
    setEventStatus,
    themeId,
    setThemeId,
    selectedEventId,
    setSelectedEventId,
    expandedTaskId,
    setExpandedTaskId,
    events,
    tasks,
    themes,
    filteredTasks,
    filteredEvents,
    taskComponentMap,
    eventComponentMap,
    allComponents,
    taskEventDateMap,
    handleComponentClick,
    handleEventClick,
    handleOpenTimeline,
    selectClass,
    isLoading,
    eventsError,
    activeItemCount,
    totalItemCount,
    showDashboard,
    setShowDashboard,
    eventStats,
    taskStats,
    taskStatsLoading,
  };
}
